import { Field, Question } from './jotform.state'

export type JotFormControl = JotFormField | JotFormButton

type JotFormField =
  | JotFormInput
  | JotFormTextarea
  | JotFormFullName
  | JotFormNumber
  | JotFormSelect
  | JotFormEmail
  | JotFormPhone
  | JotFormRadio
  | JotFormCheckbox

type JotFormInput = {
  type: 'control_textbox'
  validation: Validation
  minsize: string
  maxsize: string
} & JotFormBaseTypes &
  JotFormBaseFieldTypes

type JotFormTextarea = {
  type: 'control_textarea'
  validation: Validation
  minsize: string
  maxsize: string
} & JotFormBaseTypes &
  JotFormBaseFieldTypes

type JotFormFullName = {
  type: 'control_fullname'
  minsize: string
  maxsize: string
  sublabels: {
    prefix: string
    first: string
    middle: string
    last: string
    suffix: string
  }
  // Optional name fields
  prefix: YesNo
  prefixChoices: string
  middle: YesNo
  suffix: YesNo
} & JotFormBaseTypes &
  JotFormBaseFieldTypes

type JotFormNumber = {
  type: 'control_number'
  minValue: string
  maxValue: string
} & JotFormBaseTypes &
  JotFormBaseFieldTypes

type JotFormSelect = {
  type: 'control_dropdown'
  emptyText: string
  options: string
  selected: string
} & JotFormBaseTypes &
  JotFormBaseFieldTypes

type JotFormEmail = {
  type: 'control_email'
  maxsize: string
  confirmation: YesNo
  confirmationHint: string
  confirmationSublabel: string
} & JotFormBaseTypes &
  JotFormBaseFieldTypes

type JotFormPhone = {
  type: 'control_phone'
  compoundHint: string
} & JotFormBaseTypes &
  JotFormBaseFieldTypes

type JotFormRadio = {
  type: 'control_radio'
  options: string
  selected: string
  allowOther: YesNo
  otherText: string
} & JotFormBaseTypes &
  JotFormBaseFieldTypes

type JotFormCheckbox = {
  type: 'control_checkbox'
  options: string
  allowOther: YesNo
  otherText: string
  selected: string
} & JotFormBaseTypes &
  JotFormBaseFieldTypes

type JotFormButton = {
  type: 'control_button'
  text: string
  clear: YesNo
  clearText: string
} & JotFormBaseTypes

type JotFormBaseTypes = {
  name: string
  order: string
  hidden?: YesNo
  qid: string
}

type JotFormBaseFieldTypes = {
  readonly: YesNo
  required: YesNo
  text: string
  hint: string
  subLabel?: string
  defaultValue: string
}

type YesNo = 'Yes' | 'No'

type Validation = 'None' | 'Email' | 'Numeric' | 'Url' | 'Currency' | 'Alphabetic' | 'Alphanumeric' | 'Cyrillic'

export const parseJotformField = (jotformField: JotFormControl): Question => {
  switch (jotformField.type) {
    case 'control_textbox':
      return {
        kind: 'input',
        id: jotformField.qid,
        label: jotformField.text,
        value: jotformField.defaultValue || '',
        placeholder: jotformField.hint || undefined,
        maxLength: jotformField.maxsize ? parseInt(jotformField.maxsize) : undefined,
        fieldMessage: jotformField.subLabel,
        required: jotformField.required == 'Yes',
        readonly: jotformField.readonly == 'Yes',
        hidden: jotformField.hidden == 'Yes' || undefined,
        showErrors: false,
      }
    case 'control_textarea':
      return {
        kind: 'textarea',
        id: jotformField.qid,
        label: jotformField.text,
        value: jotformField.defaultValue || '',
        placeholder: jotformField.hint || undefined,
        maxLength: jotformField.maxsize ? parseInt(jotformField.maxsize) : undefined,
        fieldMessage: jotformField.subLabel,
        required: jotformField.required == 'Yes',
        readonly: jotformField.readonly == 'Yes',
        hidden: jotformField.hidden == 'Yes' || undefined,
        showErrors: false,
      }
    case 'control_number':
      return {
        kind: 'number',
        id: jotformField.qid,
        label: jotformField.text,
        value: jotformField.defaultValue || '',
        minValue: jotformField.minValue || undefined,
        maxValue: jotformField.maxValue || undefined,
        placeholder: jotformField.hint || undefined,
        fieldMessage: jotformField.subLabel,
        required: jotformField.required == 'Yes',
        readonly: jotformField.readonly == 'Yes',
        hidden: jotformField.hidden == 'Yes' || undefined,
        showErrors: false,
      }
    case 'control_dropdown':
      return {
        kind: 'select',
        id: jotformField.qid,
        label: jotformField.text,
        value: jotformField.defaultValue || '',
        options: jotformField.options.split('|'),
        placeholder: jotformField.emptyText || undefined,
        fieldMessage: jotformField.subLabel,
        required: jotformField.required == 'Yes',
        readonly: jotformField.readonly == 'Yes',
        hidden: jotformField.hidden == 'Yes' || undefined,
        showErrors: false,
      }
    case 'control_email':
      return {
        kind: 'email',
        id: jotformField.qid,
        label: jotformField.text,
        value: jotformField.defaultValue || '',
        maxLength: jotformField.maxsize ? parseInt(jotformField.maxsize) : undefined,
        placeholder: jotformField.hint || undefined,
        fieldMessage: jotformField.subLabel,
        required: jotformField.required == 'Yes',
        readonly: jotformField.readonly == 'Yes',
        hidden: jotformField.hidden == 'Yes' || undefined,
        extraConfirmationField:
          jotformField.confirmation == 'Yes'
            ? {
                label: jotformField.confirmationSublabel || '',
                value: '',
                placeholder: jotformField.confirmationHint || '',
                showErrors: false,
              }
            : undefined,
        showErrors: false,
      }
    case 'control_phone':
      return {
        kind: 'phone',
        id: jotformField.qid,
        label: jotformField.text,
        value: jotformField.defaultValue || '',
        placeholder: jotformField.hint || jotformField.compoundHint || undefined,
        fieldMessage: jotformField.subLabel,
        required: jotformField.required == 'Yes',
        readonly: jotformField.readonly == 'Yes',
        hidden: jotformField.hidden == 'Yes' || undefined,
        showErrors: false,
      }

    case 'control_fullname':
      return {
        kind: 'fullName',
        id: jotformField.qid,
        label: jotformField.text,
        subFields: {
          prefix:
            jotformField.prefix === 'Yes'
              ? {
                  label: jotformField.sublabels.prefix,
                  value: jotformField.prefixChoices ? jotformField.prefixChoices.split('|')[0] : '',
                  options: jotformField.prefixChoices ? jotformField.prefixChoices.split('|') : undefined,
                }
              : undefined,
          first: { label: jotformField.sublabels.first, value: '', showErrors: false },
          middle: jotformField.middle === 'Yes' ? { label: jotformField.sublabels.middle, value: '' } : undefined,
          last: { label: jotformField.sublabels.last, value: '', showErrors: false },
          suffix: jotformField.suffix === 'Yes' ? { label: jotformField.sublabels.suffix, value: '' } : undefined,
        },
        fieldMessage: jotformField.subLabel,
        required: jotformField.required == 'Yes',
        readonly: jotformField.readonly == 'Yes',
        hidden: jotformField.hidden == 'Yes' || undefined,
        showErrors: false,
      }
    case 'control_radio':
      return {
        kind: 'radio',
        id: jotformField.qid,
        label: jotformField.text,
        value: jotformField.selected || '',
        options: jotformField.options.split('|'),
        extraOption:
          jotformField.allowOther == 'Yes'
            ? { label: jotformField.otherText, value: '', showErrors: false }
            : undefined,
        fieldMessage: jotformField.subLabel,
        required: jotformField.required == 'Yes',
        readonly: jotformField.readonly == 'Yes',
        hidden: jotformField.hidden == 'Yes' || undefined,
        showErrors: false,
      }
    case 'control_checkbox':
      return {
        kind: 'checkbox',
        id: jotformField.qid,
        label: jotformField.text,
        value: jotformField.selected ? jotformField.selected.split('|').map((s) => s.substring(0, s.length - 2)) : [],
        options: jotformField.options.split('|'),
        extraOption:
          jotformField.allowOther == 'Yes'
            ? { label: jotformField.otherText, value: '', showErrors: false }
            : undefined,
        fieldMessage: jotformField.subLabel,
        required: jotformField.required == 'Yes',
        readonly: jotformField.readonly == 'Yes',
        hidden: jotformField.hidden == 'Yes' || undefined,
        showErrors: false,
      }
    case 'control_button':
      return {
        kind: 'submitButton',
        label: jotformField.text,
        hidden: jotformField.hidden == 'Yes' || undefined,
      }
  }
}

export const parseFormBuilderField = (
  field: Field
): { type: JotFormField['type']; keys: string[]; value: string[] } => {
  switch (field.kind) {
    case 'input': {
      return {
        type: 'control_textbox',
        keys: [field.id],
        value: [field.value],
      }
    }
    case 'textarea': {
      return {
        type: 'control_textarea',
        keys: [field.id],
        value: [field.value],
      }
    }
    case 'email': {
      return {
        type: 'control_email',
        keys: [field.id],
        value: [field.value],
      }
    }
    case 'phone': {
      return {
        type: 'control_phone',
        keys: [field.id],
        value: [field.value],
      }
    }
    case 'number': {
      return {
        type: 'control_number',
        keys: [field.id],
        value: [field.value],
      }
    }
    case 'select': {
      return {
        type: 'control_dropdown',
        keys: [field.id],
        value: [field.value],
      }
    }
    case 'fullName': {
      return {
        type: 'control_fullname',
        keys: Object.keys(field.subFields).map((subField) => (subField ? `${field.id}_${subField}` : '')),
        value: Object.values(field.subFields).map((subField) => (subField ? subField.value : '')),
      }
    }
    case 'radio': {
      const value = field.extraOption && field.extraOption.label == field.value ? field.extraOption.value : field.value
      return {
        type: 'control_radio',
        keys: [field.id],
        value: [value],
      }
    }
    case 'checkbox': {
      const values =
        field.extraOption && field.extraOption.value
          ? [...field.value.filter((val) => val != field.extraOption?.label), field.extraOption.value]
          : field.value
      return {
        type: 'control_checkbox',
        keys: [field.id],
        value: values,
      }
    }
  }
}
